import { useLayoutEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, Outlet, useParams, useSearchParams } from "react-router-dom";
import {
  AuthProvider,
  useAuth,
} from "./features/core/auth/context/AuthContext";
import { ViewProvider, useView } from "./context/ViewContext";
import { ThemeProvider } from "./context/ThemeContext";
import { LucideProvider } from "lucide-react";
import { ChatProvider } from "./features/support/context/ChatContext";
const UnifiedChatWidget = lazy(() => import("./features/support/components/UnifiedChatWidget"));
import AppLayout from "./components/layout/AppLayout";
import { LoadingFallback } from "./components/layout/LoadingFallback";

// Core
const Login = lazy(() => import("./features/core/auth/pages/Login"));
const OAuthAuthorize = lazy(() => import("./features/core/auth/pages/OAuthAuthorize"));
const OAuthCallback = lazy(() => import("./features/core/auth/pages/OAuthCallback"));
const ForgotPassword = lazy(() => import("./features/core/auth/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./features/core/auth/pages/ResetPassword"));
const MagicLinkVerify = lazy(() => import("./features/core/auth/pages/MagicLinkVerify"));

// Inbox
const Inbox = lazy(() => import("./features/inbox/pages/Inbox"));

// Listings
const Listings = lazy(() => import("./features/listings/pages/Listings"));
const Properties = lazy(() => import("./features/properties/pages/Properties"));
const PropertyWizard = lazy(() => import("./features/properties/wizard/PropertyWizard"));
const NewPropertyPage = lazy(() => import("./features/properties/wizard/NewPropertyPage"));
const PropertyEditorPage = lazy(() => import("./features/properties/editor/PropertyEditorPage"));
const PropertyPreviewPage = lazy(() => import("./features/properties/editor/PropertyPreviewPage"));

// Dossiers / CRM
const DossiersKanban = lazy(() => import("./features/dossiers/pages/DossiersKanban"));
const DossierDetail = lazy(() => import("./features/dossiers/pages/DossierDetail"));
const PipelineBuilder = lazy(() => import("./features/dossiers/pages/PipelineBuilder"));

// Applications
const Applications = lazy(() => import("./features/applications/pages/Applications"));
const ApplicationDetail = lazy(() => import("./features/applications/pages/ApplicationDetail"));
const WorkflowBuilder = lazy(() => import("./features/applications/pages/WorkflowBuilder"));

// Rentals
const Rentals = lazy(() => import("./features/rentals/pages/Rentals"));
const RentalDetail = lazy(() => import("./features/rentals/pages/RentalDetail"));
const Contracts = lazy(() => import("./features/rentals/contracts/pages/Contracts"));
const ContractDetail = lazy(() => import("./features/rentals/contracts/pages/ContractDetail"));
const Payments = lazy(() => import("./features/rentals/payments/pages/Payments"));
const TenantLeaseDetail = lazy(() => import("./features/rentals/pages/TenantLeaseDetail"));
const TenantPayments = lazy(() => import("./features/rentals/payments/pages/TenantPayments"));

// Maintenance
const Maintenance = lazy(() => import("./features/maintenance/pages/Maintenance"));
const MaintenanceDetail = lazy(() => import("./features/maintenance/pages/MaintenanceDetail"));
const Vendors = lazy(() => import("./features/maintenance/pages/Vendors"));
const MaintenanceProjects = lazy(() => import("./features/maintenance/pages/MaintenanceProjects"));
const MaintenanceProjectDetails = lazy(() => import("./features/maintenance/pages/MaintenanceProjectDetails"));
const MaintenanceCoordinator = lazy(() => import("./features/maintenance/pages/MaintenanceCoordinator"));
const StandingServices = lazy(() => import("./features/maintenance/pages/StandingServices"));
const VendorStatements = lazy(() => import("./features/maintenance/pages/VendorStatements"));
const VendorCockpit = lazy(() => import("./features/maintenance/pages/VendorCockpit"));

// Calendar & Settings
const Calendar = lazy(() => import("./features/calendar/pages/Calendar"));
const CalendarHubLayout = lazy(() => import("./features/calendar/pages/CalendarHubLayout"));
const AvailabilityEditor = lazy(() => import("./features/calendar/pages/AvailabilityEditor"));
const ScheduleEditor = lazy(() => import("./features/calendar/pages/ScheduleEditor"));
const EventTypeManagement = lazy(() => import("./features/calendar/pages/EventTypeManagement"));
const Settings = lazy(() => import("./features/settings/pages/Settings"));
const PlansPage = lazy(() => import("./features/memberships/pages/Plans"));

// Landing Page Editor
const LandingPageEditor = lazy(() => import("./features/landing-page/pages/LandingPageEditor"));

// Organizations, Support, Tasks, Databases
const Organizations = lazy(() => import("./features/organizations/pages/Organizations"));
const AcceptInvitation = lazy(() => import("./features/organizations/pages/AcceptInvitation"));
const Support = lazy(() => import("./features/support/pages/Support"));
const SupportDetail = lazy(() => import("./features/support/pages/SupportDetail"));
const HelpCenter = lazy(() => import("./features/support/pages/HelpCenter"));
const ResidentSupport = lazy(() => import("./features/support/pages/ResidentSupport"));
const Tasks = lazy(() => import("./features/tasks/pages/Tasks"));
const Databases = lazy(() => import("./features/databases/pages/Databases"));
const Issues = lazy(() => import("./features/core/issues/pages/Issues"));

// HomySign
const TemplateLibrary = lazy(() => import("./features/homysign/pages/TemplateLibrary"));
const TemplateEditor = lazy(() => import("./features/homysign/pages/TemplateEditor"));
const DocumentDetail = lazy(() => import("./features/homysign/pages/DocumentDetail"));

// HomyTrust
const TrustProfilePage = lazy(() => import("./features/homytrust/pages/TrustProfilePage"));

// Public (token-gated portals; org/property/booking pages live on homy-landing)
const PublicShare = lazy(() => import("./features/public/pages/PublicShare"));
const PublicDocumentView = lazy(() => import("./features/public/pages/PublicDocumentView"));
const PublicApplicationPortal = lazy(() => import("./features/public/pages/PublicApplicationPortal"));
const PublicGuarantorIntake = lazy(() => import("./features/public/pages/PublicGuarantorIntake"));
const PublicFormRenderer = lazy(() => import("./features/public/pages/PublicFormRenderer"));
const NotFoundPage = lazy(() => import("./components/layout/NotFoundPage"));
import { PUBLIC_SHARE_ROUTE } from "./constants/sharing";
import PublicLayout from "./layouts/PublicLayout";

import { Spinner } from "@homy/ui";
import { RequirePermission } from "@/components/auth/PermissionGate";
import { Profile } from "@/hooks/usePermissions";

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-transparent">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function ManagementRoute({ children, redirectTo = "/" }) {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-transparent">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isManagement =
    Boolean(user?.isPlatformAdmin) ||
    ["PLATFORM_ADMIN", "SUPER_ADMIN", "ORG_ADMIN", "ADMIN", "MANAGER", "OWNER"].includes(
      user?.role
    ) ||
    Boolean(user?.roles?.some?.((r) =>
      ["PLATFORM_ADMIN", "SUPER_ADMIN", "ORG_ADMIN", "ADMIN", "MANAGER", "OWNER"].includes(r)
    )) ||
    Boolean(user?.assignedRoles?.some?.((r) =>
      ["PLATFORM_ADMIN", "SUPER_ADMIN", "ORG_ADMIN", "ADMIN", "MANAGER", "OWNER"].includes(r)
    ));

  if (!isManagement) {
    const isVendor =
      Boolean(user?.isVendor) ||
      user?.role === "VENDOR" ||
      user?.role === "CONTRACTOR";
    const target =
      typeof redirectTo === "function"
        ? redirectTo(user)
        : isVendor && redirectTo === "/tenant/support"
        ? "/"
        : redirectTo;
    return <Navigate to={target} replace />;
  }

  return children;
}

function AppIndex() {
  return <Inbox />;
}

function MaintenanceRequestRedirect() {
  const { id } = useParams();
  return <Navigate to={`/maintenance/${id}`} replace />;
}

const LEGACY_PROPERTY_SECTION_TO_PATH = {
  // details sections & aliases
  photos: "details/photos",
  media: "details/photos",
  title: "details/title",
  general: "details/title",
  details: "details/details",
  basics: "details/details",
  areas: "details/areas",
  units: "details/areas",
  documents: "details/areas",
  location: "details/location",
  amenities: "details/amenities",
  features: "details/amenities",
  // listings sections & aliases
  offers: "listings/offers",
  pricing: "listings/offers",
  portals: "listings/offers",
  syndication: "listings/offers",
  history: "listings/offers",
  terms: "listings/terms",
  lease: "listings/terms",
  visits: "listings/visits",
  // management sections & aliases
  "rent-record": "management/rent-record",
  financial: "management/rent-record",
  utilities: "management/utilities",
  operations: "management/utilities",
  "smart-locks": "management/smart-locks",
  maintenance: "management/smart-locks",
  iot: "management/smart-locks",
  settings: "management/settings",
};

const LEGACY_PROPERTY_TAB_TO_PATH = {
  // Canonical tabs
  details: "details",
  listings: "listings",
  management: "management",
  preview: "preview",
  property: "details",
  // Legacy tab names & section aliases
  overview: "details/details",
  basics: "details/details",
  title: "details/title",
  general: "details/title",
  location: "details/location",
  photos: "details/photos",
  media: "details/photos",
  amenities: "details/amenities",
  features: "details/amenities",
  areas: "details/areas",
  units: "details/areas",
  documents: "details/areas",
  offers: "listings/offers",
  pricing: "management/rent-record",
  portals: "listings/offers",
  syndication: "listings/offers",
  history: "listings/offers",
  terms: "listings/terms",
  lease: "listings/terms",
  visits: "listings/visits",
  settings: "management/settings",
  financial: "management/rent-record",
  "rent-record": "management/rent-record",
  utilities: "management/utilities",
  operations: "management/utilities",
  "smart-locks": "management/smart-locks",
  maintenance: "management/smart-locks",
  iot: "management/smart-locks",
};

function PropertyRootRedirect() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const legacyTab = searchParams.get("tab");
  const legacySection = searchParams.get("section");
  let target = "details";
  if (legacySection && LEGACY_PROPERTY_SECTION_TO_PATH[legacySection]) {
    target = LEGACY_PROPERTY_SECTION_TO_PATH[legacySection];
  } else if (legacyTab && LEGACY_PROPERTY_TAB_TO_PATH[legacyTab]) {
    target = LEGACY_PROPERTY_TAB_TO_PATH[legacyTab];
  }
  const remaining = new URLSearchParams(searchParams);
  remaining.delete("tab");
  remaining.delete("section");
  const queryString = remaining.toString();
  const search = queryString ? `?${queryString}` : "";
  return <Navigate to={`/properties/${id}/${target}${search}`} replace />;
}

function PropertyEditRedirect() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const legacySection = searchParams.get("section");
  const legacyTab = searchParams.get("tab");
  let target = "details";
  if (legacySection && LEGACY_PROPERTY_SECTION_TO_PATH[legacySection]) {
    target = LEGACY_PROPERTY_SECTION_TO_PATH[legacySection];
  } else if (legacyTab && LEGACY_PROPERTY_TAB_TO_PATH[legacyTab]) {
    target = LEGACY_PROPERTY_TAB_TO_PATH[legacyTab];
  }
  const remaining = new URLSearchParams(searchParams);
  remaining.delete("tab");
  remaining.delete("section");
  const queryString = remaining.toString();
  const search = queryString ? `?${queryString}` : "";
  return <Navigate to={`/properties/${id}/${target}${search}`} replace />;
}

function PropertyLegacySectionRedirect() {
  const { id, section } = useParams();
  const [searchParams] = useSearchParams();
  const target = (section && LEGACY_PROPERTY_SECTION_TO_PATH[section]) || "details";
  const queryString = searchParams.toString();
  const search = queryString ? `?${queryString}` : "";
  return <Navigate to={`/properties/${id}/${target}${search}`} replace />;
}

function App() {
  useLayoutEffect(() => {
    if (typeof document !== "undefined" && !document.documentElement.getAttribute("data-theme")) {
      document.documentElement.setAttribute("data-theme", "terracotta");
    }
  }, []);

  return (
    <AuthProvider>
      <ThemeProvider>
        <LucideProvider strokeWidth={1.75}>
          <ViewProvider>
            <ChatProvider>
              <Suspense fallback={<LoadingFallback />}>
                <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Login />} />
            <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/auth/callback/:provider" element={<OAuthCallback />} />
            <Route path="/data-deletion" element={<OAuthCallback />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/magic-link" element={<MagicLinkVerify />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            {/* The public org / property / booking pages moved to the marketing
                site (homy-landing). What remains here are token-gated portals. */}
            <Route element={<PublicLayout />}>
          <Route path={PUBLIC_SHARE_ROUTE} element={<PublicShare />} />
          <Route path="/public/homysign/:token" element={<PublicDocumentView />} />
          <Route path="/sign/:token" element={<PublicDocumentView />} />
          <Route path="/homysign/sign/:token" element={<PublicDocumentView />} />
          <Route path="/public/applications/:token" element={<PublicApplicationPortal />} />
          <Route path="/public/guarantor/:token" element={<PublicGuarantorIntake />} />
          <Route path="/public/forms/:token" element={<PublicFormRenderer />} />
          <Route path="/help" element={<HelpCenter />} />
          <Route path="/support/help-center" element={<HelpCenter />} />
        </Route>

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<AppIndex />} />
          <Route path="inbox" element={<Navigate to="/" replace />} />
          <Route
            path="listings"
            element={
              <ManagementRoute>
                <Listings />
              </ManagementRoute>
            }
          />
          <Route
            path="properties"
            element={
              <ManagementRoute>
                <Properties />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/new"
            element={
              <ManagementRoute>
                <NewPropertyPage />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id"
            element={
              <ManagementRoute>
                <PropertyRootRedirect />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/edit"
            element={
              <ManagementRoute>
                <PropertyEditRedirect />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/setup/:step?"
            element={
              <ManagementRoute>
                <PropertyWizard />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/details"
            element={
              <ManagementRoute>
                <PropertyEditorPage tab="details" />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/details/:section"
            element={
              <ManagementRoute>
                <PropertyEditorPage tab="details" />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/listings"
            element={
              <ManagementRoute>
                <PropertyEditorPage tab="listings" />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/listings/:section"
            element={
              <ManagementRoute>
                <PropertyEditorPage tab="listings" />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/property"
            element={
              <ManagementRoute>
                <PropertyEditRedirect />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/property/:section"
            element={
              <ManagementRoute>
                <PropertyLegacySectionRedirect />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/management"
            element={
              <ManagementRoute>
                <PropertyEditorPage tab="management" />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/management/:section"
            element={
              <ManagementRoute>
                <PropertyEditorPage tab="management" />
              </ManagementRoute>
            }
          />
          <Route
            path="properties/:id/preview"
            element={
              <ManagementRoute>
                <PropertyPreviewPage />
              </ManagementRoute>
            }
          />
          <Route
            path="landing-page"
            element={
              <ManagementRoute>
                <LandingPageEditor />
              </ManagementRoute>
            }
          />
          <Route
            path="pipelines"
            element={
              <ManagementRoute>
                <DossiersKanban />
              </ManagementRoute>
            }
          />
          <Route
            path="pipelines/builder"
            element={
              <ManagementRoute>
                <PipelineBuilder />
              </ManagementRoute>
            }
          />
          <Route
            path="pipelines/builder/:id"
            element={
              <ManagementRoute>
                <PipelineBuilder />
              </ManagementRoute>
            }
          />
          <Route
            path="pipelines/:id"
            element={
              <ManagementRoute>
                <DossierDetail />
              </ManagementRoute>
            }
          />
          <Route
            path="applications"
            element={
              <ManagementRoute>
                <Applications />
              </ManagementRoute>
            }
          />
          <Route
            path="applications/workflows"
            element={
              <ManagementRoute>
                <WorkflowBuilder />
              </ManagementRoute>
            }
          />
          <Route
            path="applications/:id"
            element={
              <ManagementRoute>
                <ApplicationDetail />
              </ManagementRoute>
            }
          />
          <Route
            path="rentals"
            element={
              <ManagementRoute>
                <Rentals />
              </ManagementRoute>
            }
          />
          <Route
            path="rentals/:id"
            element={
              <ManagementRoute>
                <RentalDetail />
              </ManagementRoute>
            }
          />
          <Route
            path="rentals/contracts"
            element={
              <ManagementRoute>
                <Contracts />
              </ManagementRoute>
            }
          />
          <Route
            path="rentals/contracts/:id"
            element={
              <ManagementRoute>
                <ContractDetail />
              </ManagementRoute>
            }
          />
          <Route
            path="rentals/payments"
            element={
              <ManagementRoute>
                <Payments />
              </ManagementRoute>
            }
          />
          <Route
            path="tenant/lease"
            element={
              <RequirePermission profile={Profile.RESIDENT}>
                <TenantLeaseDetail />
              </RequirePermission>
            }
          />
          <Route path="tenant/leases" element={<Navigate to="/tenant/lease" replace />} />
          <Route
            path="tenant/payments"
            element={
              <RequirePermission profile={Profile.RESIDENT}>
                <TenantPayments />
              </RequirePermission>
            }
          />
          <Route
            path="tenant/support"
            element={
              <RequirePermission profile={Profile.RESIDENT}>
                <ResidentSupport />
              </RequirePermission>
            }
          />
          <Route
            path="tenant/support/:id"
            element={
              <RequirePermission profile={Profile.RESIDENT}>
                <ResidentSupport />
              </RequirePermission>
            }
          />
          <Route path="resident/support" element={<Navigate to="/tenant/support" replace />} />
          <Route path="resident/support/:id" element={<Navigate to="/tenant/support" replace />} />
          <Route path="calendar" element={<CalendarHubLayout />}>
            <Route index element={<Calendar />} />
            <Route
              path="availability/:scheduleId"
              element={<ScheduleEditor />}
            />
            <Route path="availability" element={<AvailabilityEditor />} />
            <Route path="event-types" element={<EventTypeManagement />} />
          </Route>
          <Route path="settings" element={<Settings />} />
          <Route path="settings/plans" element={<PlansPage />} />
          <Route path="trust" element={<TrustProfilePage />} />
          <Route path="plans" element={<PlansPage />} />
          {/*

            Static maintenance paths MUST come before maintenance/:id so "projects"
            and "vendors" are not interpreted as ticket IDs.
          */}
          <Route path="maintenance" element={<Maintenance />} />
          <Route
            path="maintenance/coordinator"
            element={
              <ProtectedRoute>
                <MaintenanceCoordinator />
              </ProtectedRoute>
            }
          />
          <Route
            path="maintenance/projects"
            element={
              <RequirePermission profile={Profile.MANAGEMENT}>
                <MaintenanceProjects />
              </RequirePermission>
            }
          />
          <Route
            path="maintenance/projects/:projectId"
            element={
              <RequirePermission profile={[Profile.MANAGEMENT, Profile.VENDOR]}>
                <MaintenanceProjectDetails />
              </RequirePermission>
            }
          />
          <Route
            path="maintenance/vendors"
            element={
              <ManagementRoute>
                <Vendors />
              </ManagementRoute>
            }
          />
          <Route
            path="vendors"
            element={<Navigate to="/maintenance/vendors" replace />}
          />
          <Route
            path="maintenance/services"
            element={
              <RequirePermission profile={Profile.MANAGEMENT}>
                <StandingServices />
              </RequirePermission>
            }
          />
          <Route
            path="maintenance/statements"
            element={
              <RequirePermission
                anyPermissions={["financial:ledger:read", "maintenance:budget:read"]}
              >
                <VendorStatements />
              </RequirePermission>
            }
          />
          <Route
            path="vendor/statements"
            element={<Navigate to="/maintenance/statements" replace />}
          />
          <Route
            path="vendor/cockpit"
            element={<Navigate to="/" replace />}
          />
          <Route
            path="vendor/calendar"
            element={<Navigate to="/calendar" replace />}
          />
          <Route path="maintenance/:id" element={<MaintenanceDetail />} />
          <Route path="maintenance/requests/:id" element={<MaintenanceRequestRedirect />} />
          <Route
            path="organizations"
            element={
              <ManagementRoute>
                <Organizations />
              </ManagementRoute>
            }
          />
          <Route
            path="support"
            element={
              <ManagementRoute redirectTo="/tenant/support">
                <Support />
              </ManagementRoute>
            }
          />
          <Route
            path="support/inbox"
            element={
              <ManagementRoute redirectTo="/tenant/support">
                <Support />
              </ManagementRoute>
            }
          />
          <Route
            path="support/:id"
            element={
              <ManagementRoute redirectTo="/tenant/support">
                <SupportDetail />
              </ManagementRoute>
            }
          />
          <Route path="support/help-center" element={<HelpCenter />} />
          <Route path="help" element={<HelpCenter />} />
          <Route path="tasks" element={<Tasks />} />
          <Route path="tasks/:taskId" element={<Tasks />} />
          <Route
            path="databases"
            element={
              <ManagementRoute>
                <Databases />
              </ManagementRoute>
            }
          />
          <Route
            path="databases/:dbId"
            element={
              <ManagementRoute>
                <Databases />
              </ManagementRoute>
            }
          />
          <Route
            path="databases/:dbId/tables/:tableId"
            element={
              <ManagementRoute>
                <Databases />
              </ManagementRoute>
            }
          />
          <Route
            path="issues"
            element={
              <ManagementRoute>
                <Issues />
              </ManagementRoute>
            }
          />
          <Route
            path="homysign/templates"
            element={
              <ManagementRoute>
                <TemplateLibrary />
              </ManagementRoute>
            }
          />
          <Route
            path="homysign/templates/:id/edit"
            element={
              <ManagementRoute>
                <TemplateEditor />
              </ManagementRoute>
            }
          />
          <Route
            path="homysign/documents/:id"
            element={
              <ManagementRoute>
                <DocumentDetail />
              </ManagementRoute>
            }
          />
          <Route
            path="homysign/documents"
            element={<Navigate to="/homysign/templates" replace />}
          />
          <Route
            path="app/homysign/templates"
            element={<Navigate to="/homysign/templates" replace />}
          />
          <Route
            path="app/homysign/templates/:id/edit"
            element={
              <ManagementRoute>
                <TemplateEditor />
              </ManagementRoute>
            }
          />
          <Route
            path="app/homysign/documents"
            element={<Navigate to="/homysign/templates" replace />}
          />
          <Route
            path="app/homysign/documents/:id"
            element={
              <ManagementRoute>
                <DocumentDetail />
              </ManagementRoute>
            }
          />
          <Route
            path="app/homysign/*"
            element={<Navigate to="/homysign/templates" replace />}
          />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
      <Suspense fallback={null}>
        <UnifiedChatWidget />
      </Suspense>
    </ChatProvider>
  </ViewProvider>
  </LucideProvider>
  </ThemeProvider>
</AuthProvider>
);
}

export { ManagementRoute, ManagementRoute as RequireManagementRoute };
export default App;
